<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">Pacientes</h1>
        <p class="nd-page-kicker">Cadastros, histórico e vínculos clínicos.</p>
      </div>
      <v-btn id="btnNew" type="button" color="primary" elevation="0" to="/pacientes/novo">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid module="patients" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Lista de pacientes</div>
          <div class="nd-table-toolbar__subtitle">Busca rápida por nome, CPF ou registro</div>
        </div>
        <v-form class="nd-table-toolbar__actions nd-table-toolbar__actions--wide" @submit.prevent="load()">
          <v-text-field
            dense
            id="name"
            placeholder="Buscar paciente"
            prepend-inner-icon="mdi-magnify"
            v-model="dataList.filter.name" hide-details="auto"
            outlined
          ></v-text-field>
          <v-text-field
            dense
            id="registration"
            placeholder="CPF ou registro"
            v-model="dataList.filter.registration" hide-details="auto"
            outlined
          ></v-text-field>
          <v-btn id="btnFilter" type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="dataList.headers"
              :items="dataList.data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="dataList.count"
              :disable-sort="true"
              :loading="dataList.loading"
              no-data-text="Nenhum paciente encontrado"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="dataList.options"
              :footer-props="dataList.footerOptions"
            >
              <template v-slot:[`item.name`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ getInitials(item.name) }}</span>
                  <div>
                    <strong>{{ item.name }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.registration`]="{ item }">
                <span>{{ formatCpf(item.registration) }}</span>
              </template>

              <template v-slot:[`item.phone`]="{ item }">
                <span>{{ formatPhone(item.phone && item.phone.value) }}</span>
              </template>

              <template v-slot:[`item.guardian`]="{ item }">
                <span v-if="isMinor(item.birthday)">
                  <div v-if="item.guardianName">{{ item.guardianName }}</div>
                  <div v-if="item.guardianCpf" class="caption grey--text">{{ formatCpf(item.guardianCpf) }}</div>
                  <div v-if="item.guardianPhone" class="caption grey--text">{{ formatPhone(item.guardianPhone) }}</div>
                </span>
                <span v-else>—</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="editItem(item)">
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar Dados</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deleteItem(item)">
                        <v-icon size="19">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover Paciente</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="generateCompletionLink(item)" :loading="completionLink.loadingId === item.id" color="primary">
                        <v-icon size="19">mdi-link-variant-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Gerar link de cadastro</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewProfile(item)" color="primary">
                        <v-icon size="19">mdi-account-details-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver Perfil</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>

    <v-dialog v-model="completionLink.dialog" max-width="620">
      <v-card class="patient-link-dialog">
        <v-card-title>
          <div>
            <small>Finalização de cadastro</small>
            <h2>Link do paciente</h2>
          </div>
          <v-btn icon @click="completionLink.dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p>
            Envie este link para que o paciente complete os dados cadastrais com segurança.
            O acesso expira em 7 dias.
          </p>
          <v-text-field
            label="Link de finalização"
            :value="completionLink.url"
            readonly
            outlined
            dense
            hide-details="auto"
            append-icon="mdi-content-copy"
            @click:append="copyCompletionLink"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined elevation="0" @click="completionLink.dialog = false">Fechar</v-btn>
          <v-btn color="primary" elevation="0" @click="copyCompletionLink">
            <v-icon left size="18">mdi-content-copy</v-icon>
            Copiar link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import Vue from "vue";
import VueLodash from "vue-lodash";
import _ from "lodash";
import axios from "axios";

import object from "@/common/util/object";

Vue.use(VueLodash, { lodash: _ });

export default {
  components: { ModuleStatGrid },
  name: "PatientList",

  data: () => ({
    dataset: {
      data: {},
    },
    dataList: {
      filter: {
        name: "",
        registration: "",
      },
      headers: [
        { text: "Paciente", value: "name" },
        { text: "CPF", value: "registration", sortable: false },
        { text: "Telefone", value: "phone", sortable: false },
        { text: "Responsável", value: "guardian", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: {
        page: 1,
        size: 10,
        sortBy: ["id"],
        sortDesc: [false],
      },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
    completionLink: {
      dialog: false,
      loadingId: null,
      url: "",
    },
  }),

  created() {
    this.load();
  },

  computed: {
    lastRecordDate() {
      const latest = this.dataList.data.find((item) => item.date || item.created_at);
      const value = latest && (latest.date || latest.created_at);
      return value ? new Date(value).toLocaleDateString("pt-BR") : "-";
    },
  },


  methods: {
    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.size = size;
      this.load(true);
    },

    formatCpf(value) {
      if (!value) return '—';
      const digits = value.replace(/\D/g, '');
      if (digits.length !== 11) return value;
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    },

    isMinor(birthday) {
      if (!birthday) return false;
      const birth = new Date(birthday.split('T')[0] + 'T00:00:00');
      const today = new Date();
      const age = today.getFullYear() - birth.getFullYear() -
        (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
      return age < 18;
    },

    formatPhone(value) {
      if (!value) return '—';
      const digits = value.replace(/\D/g, '');
      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      }
      return value;
    },

    getInitials(name) {
      if (!name) return "P";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },

    async load() {
      const search = Object.fromEntries(
        Object.entries(this.dataList.filter).filter(([_, v]) => v)
      );

      const params = {
        page: this.dataList.options.page,
        limit: this.dataList.options.itemsPerPage,
      };

      if (!object.isEmpty(search)) {
        params.search = search;
      }

      this.dataList.loading = true;

      try {
        const result = await axios.get("/patients", {
          params: params,
        });
        this.dataList.data = result.data.data;
        this.dataList.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.dataList.loading = false;
      }
    },

    editItem(item) {
      this.$router.push(`/pacientes/${item.id}`);
    },

    async deleteItem(item) {
      const proceed = await this.$dialog.confirm({
        text: `Deseja remover o paciente "${item.name}"? Esta ação não poderá ser desfeita.`,
        title: "Confirmar exclusão",
        actions: {
          false: "Cancelar",
          true: { text: "Excluir", color: "error" },
        },
      });

      if (!proceed) return;

      try {
        await axios.delete(`/patients/${item.id}`);
        this.$dialog.notify.success("Paciente removido com sucesso!", {
          position: "top-right",
          timeout: 3000,
        });
        this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      }
    },

    viewProfile(item) {
      this.$router.push(`/pacientes/${item.id}/perfil`);
    },

    async generateCompletionLink(item) {
      this.completionLink.loadingId = item.id;

      try {
        const response = await axios.post(`/patients/${item.id}/completion-link`, {
          baseUrl: window.location.origin,
        });
        const data = response.data.data || {};
        this.completionLink.url = data.url || `${window.location.origin}/#/cadastro-paciente/${data.token}`;
        this.completionLink.dialog = true;
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.completionLink.loadingId = null;
      }
    },

    async copyCompletionLink() {
      if (!this.completionLink.url) return;

      try {
        await navigator.clipboard.writeText(this.completionLink.url);
        this.$dialog.notify.success("Link copiado com sucesso!", {
          position: "top-right",
          timeout: 3000,
        });
      } catch (error) {
        this.$dialog.notify.error("Não foi possível copiar automaticamente. Selecione o link e copie manualmente.", {
          position: "top-right",
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-link-dialog {
  border-radius: 8px;

  .v-card__title {
    align-items: flex-start;
    border-bottom: 1px solid #eef0f2;
    display: flex;
    justify-content: space-between;
    padding: 24px 28px 18px;

    small {
      color: var(--v-primary-base);
      display: block;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0;
      margin-bottom: 6px;
      text-transform: uppercase;
    }

    h2 {
      color: #101c1a;
      font-size: 24px;
      line-height: 1.15;
      margin: 0;
    }
  }

  .v-card__text {
    color: #6b7470;
    padding: 22px 28px 8px;
  }

  .v-card__actions {
    padding: 18px 28px 24px;
  }
}
</style>
